import SqFetch from 'typescript/application/sq_fetch';
import { _handleResponse } from 'typescript/clients/utilities/handle_http_response';
import { _urlContext } from 'typescript/clients/utilities/common';
import { CountryCode, LanguageCode } from 'typescript/constants/localization';
import { type TOrderSummary } from 'typescript/types/api/solidshop';

// most of these functions are controlled by the methods in this controller
// app/controllers/sq/api/checkout/order_actions_controller.rb

export const GET_ORDER_SUMMARY_BASE_URL = '/shop/api/v1/checkout/order/show';
export const UPDATE_ORDER_INFORMATION_URL =
  '/shop/api/v1/checkout/order/update';
export const COMPLETE_ORDER_URL = '/shop/api/v1/checkout/order/complete';
export const RECAPTCHA_FALLBACK_URL =
  '/shop/api/v1/checkout/order/recaptcha_fallback';

/**
 * Get Order summary
 * @returns order along with some other information
 * app/interactors/information/order_summary_interactor.rb
 */
export const getOrderSummary = async ({
  countryCode,
  languageCode,
  orderNumber,
}: {
  countryCode: CountryCode;
  languageCode: LanguageCode;
  orderNumber: string;
}): Promise<TOrderSummary> => {
  const response = await SqFetch.get(
    `${GET_ORDER_SUMMARY_BASE_URL}${_urlContext({
      countryCode,
      languageCode,
      orderNumber,
    })}`,
  );

  return _handleResponse(response);
};

/**
 * Update order address information
 * @returns success or error message
 */
export const updateOrderAddressInformation = async ({
  address,
  countryCode,
  languageCode,
  orderNumber,
}: {
  address: Record<string, string | boolean>;
  countryCode: CountryCode;
  languageCode: LanguageCode;
  orderNumber: string;
}): Promise<{ success: boolean }> => {
  const response = await SqFetch.post(UPDATE_ORDER_INFORMATION_URL, {
    checkout_information: address,
    store_code: countryCode,
    language_code: languageCode,
    order_number: orderNumber,
  });

  return _handleResponse(response);
};

/**
 * Complete Order
 * @returns success or error message
 */
export const completeOrder = async ({
  recaptchaResponse,
  financingDisclosureAcknowledged = false,
  useSavedPaymentSource = false,
  makeDefaultCard = false,
  paymentPostalCode = '',
  countryCode,
  languageCode,
  orderNumber,
}: {
  recaptchaResponse: string;
  financingDisclosureAcknowledged?: boolean;
  useSavedPaymentSource?: boolean;
  makeDefaultCard?: boolean;
  paymentPostalCode?: string;
  countryCode: CountryCode;
  languageCode: LanguageCode;
  orderNumber: string;
}): Promise<{ success: boolean }> => {
  const response = await SqFetch.post(COMPLETE_ORDER_URL, {
    recaptcha_response: recaptchaResponse,
    financing_disclosure_acknowledged: financingDisclosureAcknowledged,
    use_saved_payment_source: useSavedPaymentSource,
    set_default_payment_source: makeDefaultCard,
    payment_postal_code: paymentPostalCode,
    store_code: countryCode,
    language_code: languageCode,
    order_number: orderNumber,
    forter_token: window.ftrToken, // see app/javascript/components/typescript/types/window.d.ts
  });

  return _handleResponse(response);
};

/**
 * Request fallback challenge token when recaptcha fails
 * @returns challenge token and expiration time
 */
export const requestRecaptchaFallback = async ({
  countryCode,
  languageCode,
  orderNumber,
}: {
  countryCode: CountryCode;
  languageCode: LanguageCode;
  orderNumber: string;
}): Promise<{ challenge_token: string; expires_at: number }> => {
  const response = await SqFetch.post(RECAPTCHA_FALLBACK_URL, {
    store_code: countryCode,
    language_code: languageCode,
    order_number: orderNumber,
  });

  return _handleResponse(response);
};
